<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import EditorPageArtboard from './EditorPageArtboard.vue';
import { useEditorCanvasViewport } from '../composables/useEditorCanvasViewport';
import { SYSTEM_STAGES } from '../lib/documentModel';
import EventsAPI from 'dashboard/api/planning/events';
import { getDefaultStageColor } from 'dashboard/routes/dashboard/planning/events/useEventStageColors';

const props = defineProps({
  pages: { type: Array, default: () => [] },
  activePageId: { type: String, default: null },
  nodes: { type: Array, default: () => [] },
  nodesRevision: { type: Number, default: 0 },
  selectedId: { type: String, default: null },
  components: { type: Array, default: () => [] },
  deviceMode: { type: String, default: 'desktop' },
  theme: { type: Object, default: () => ({}) },
  templateId: { type: String, default: '' },
  selectedEventId: { type: [String, Number], default: null },
  portalEditableAttributeKeys: { type: Array, default: () => [] },
  feedbackEnabled: { type: Boolean, default: false },
  inboxId: { type: [String, Number], default: '' },
  availableStages: { type: Array, default: () => [] },
});
const emit = defineEmits([
  'select',
  'deselect',
  'insert',
  'move',
  'remove',
  'duplicate',
  'updateSpans',
  'updateContent',
  'activatePage',
  'addPage',
  'removePage',
  'reassignPageStage',
]);
const DEFAULT_PAGE_COLOR = '#64748b';
const FEEDBACK_CHROME_ID = 'portal-feedback';
const WIDGET_CHROME_ID = 'portal-assistant';

const { t } = useI18n();
const store = useStore();

const viewportRef = ref(null);
const worldRef = ref(null);
const stagePickerOpen = ref(false);
const reassignPageId = ref(null);

const {
  zoom,
  spaceHeld,
  bindWorld,
  bindViewport,
  bindZoomLabel,
  dispose,
  zoomTo,
  zoomAt,
  resetView,
  wheelPanBy,
  startPan,
  movePan,
  endPan,
  setSpaceHeld,
  fitToBounds,
  focusRect,
} = useEditorCanvasViewport();

const zoomLabelRef = ref(null);

const DESKTOP_ARTBOARD_WIDTH = 1152; // matches previous max-w-6xl preview
const MOBILE_ARTBOARD_WIDTH = 390;
const ARTBOARD_GAP = 64;
const HEADER_HEIGHT = 36;
const DEFAULT_ARTBOARD_HEIGHT = 900;

const pageHeights = ref({});
/** Page ids whose iframe has been mounted (sticky — once true, stays true). */
const mountedPageIds = ref({});
const pageShellEls = {};

const artboardWidth = computed(() =>
  props.deviceMode === 'mobile' ? MOBILE_ARTBOARD_WIDTH : DESKTOP_ARTBOARD_WIDTH
);

const pageLabel = page => {
  if (!page?.systemStage) {
    return t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.DEFAULT');
  }
  const key = `PLANNING.EVENTS.STAGES.${page.systemStage.toUpperCase()}`;
  return t(key);
};

const stageLabel = stage => {
  const key = `PLANNING.EVENTS.STAGES.${String(stage).toUpperCase()}`;
  return t(key);
};

const stageColorMap = ref(
  Object.fromEntries(
    SYSTEM_STAGES.map(stage => [stage, getDefaultStageColor(stage)])
  )
);

const loadStageColors = async () => {
  try {
    const response = await EventsAPI.statusOptions();
    const next = { ...stageColorMap.value };
    (response.data?.system_statuses || []).forEach(status => {
      const key = status.key || status.system_stage;
      if (!key) return;
      next[key] =
        status.resolved_color || status.color || getDefaultStageColor(key);
    });
    stageColorMap.value = next;
  } catch {
    // Keep defaults
  }
};

const pageLabelColor = page => {
  if (!page?.systemStage) return DEFAULT_PAGE_COLOR;
  return (
    stageColorMap.value[page.systemStage] ||
    getDefaultStageColor(page.systemStage)
  );
};

const contrastLabelText = hex => {
  const raw = String(hex || '').replace('#', '');
  if (raw.length !== 6) return '#ffffff';
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return '#ffffff';
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 160 ? '#0f172a' : '#ffffff';
};

const pageLabelStyle = page => {
  const bg = pageLabelColor(page);
  return {
    backgroundColor: bg,
    color: contrastLabelText(bg),
  };
};

const stageSwatchStyle = stage => ({
  backgroundColor: stageColorMap.value[stage] || getDefaultStageColor(stage),
});

const nodesForPage = page => {
  if (page.id === props.activePageId) return props.nodes;
  return page.nodes || [];
};

const canAddPage = computed(() => props.availableStages.length > 0);

const maxArtboardHeight = computed(() => {
  let max = DEFAULT_ARTBOARD_HEIGHT;
  props.pages.forEach(page => {
    const h = pageHeights.value[page.id] || DEFAULT_ARTBOARD_HEIGHT;
    if (h > max) max = h;
  });
  return max;
});

const artboardBounds = computed(() => {
  const count = Math.max(props.pages.length, 1);
  const width = count * artboardWidth.value + (count - 1) * ARTBOARD_GAP;
  const height = HEADER_HEIGHT + maxArtboardHeight.value;
  return { x: 0, y: 0, width, height };
});

const pageWorldRect = index => {
  const page = props.pages[index];
  const x = index * (artboardWidth.value + ARTBOARD_GAP);
  const h = (page && pageHeights.value[page.id]) || DEFAULT_ARTBOARD_HEIGHT;
  return {
    x,
    y: 0,
    width: artboardWidth.value,
    height: HEADER_HEIGHT + h,
  };
};

const onPageContentHeight = (pageId, height) => {
  if (!pageId || !height) return;
  const prev = pageHeights.value[pageId] || DEFAULT_ARTBOARD_HEIGHT;
  const next = Math.max(prev, height);
  if (pageHeights.value[pageId] === next) return;
  pageHeights.value = { ...pageHeights.value, [pageId]: next };
};

const markPageMounted = pageId => {
  if (!pageId || mountedPageIds.value[pageId]) return;
  mountedPageIds.value = { ...mountedPageIds.value, [pageId]: true };
};

const setPageShellEl = (pageId, el) => {
  if (el) pageShellEls[pageId] = el;
  else delete pageShellEls[pageId];
};

/**
 * CSS-transform pan/zoom breaks IntersectionObserver. Check visible artboards
 * against the viewport rect instead, and always preload the active page ±1.
 */
const syncVisiblePages = () => {
  const viewport = viewportRef.value;
  if (!viewport) return;

  const activeIdx = props.pages.findIndex(p => p.id === props.activePageId);
  props.pages.forEach((page, index) => {
    if (page.id === props.activePageId) {
      markPageMounted(page.id);
      return;
    }
    if (activeIdx >= 0 && Math.abs(index - activeIdx) <= 1) {
      markPageMounted(page.id);
      return;
    }
  });

  const vRect = viewport.getBoundingClientRect();
  const margin = 200;
  props.pages.forEach(page => {
    if (mountedPageIds.value[page.id]) return;
    const el = pageShellEls[page.id];
    if (!el) return;
    const r = el.getBoundingClientRect();
    const intersects =
      r.right > vRect.left - margin &&
      r.left < vRect.right + margin &&
      r.bottom > vRect.top - margin &&
      r.top < vRect.bottom + margin;
    if (intersects) markPageMounted(page.id);
  });
};

let syncVisibleRaf = 0;
const scheduleSyncVisiblePages = () => {
  if (syncVisibleRaf) return;
  syncVisibleRaf = requestAnimationFrame(() => {
    syncVisibleRaf = 0;
    syncVisiblePages();
  });
};

const isPageFrameMounted = pageId => Boolean(mountedPageIds.value[pageId]);

const fitCanvas = () => {
  const el = viewportRef.value;
  if (!el) return;
  const { width, height } = el.getBoundingClientRect();
  fitToBounds(artboardBounds.value, width, height, 56);
  scheduleSyncVisiblePages();
};

/** Start at 100% zoom, centered on the active (or first) page — reads as a real page. */
const centerAtFullZoom = () => {
  const el = viewportRef.value;
  if (!el) return;
  const { width, height } = el.getBoundingClientRect();
  zoomTo(1);
  const index = Math.max(
    0,
    props.pages.findIndex(p => p.id === props.activePageId)
  );
  focusRect(pageWorldRect(index < 0 ? 0 : index), width, height);
  scheduleSyncVisiblePages();
};

const focusPage = pageId => {
  const index = props.pages.findIndex(p => p.id === pageId);
  if (index < 0) return;
  const el = viewportRef.value;
  if (!el) return;
  const { width, height } = el.getBoundingClientRect();
  focusRect(pageWorldRect(index), width, height);
  scheduleSyncVisiblePages();
};

const onZoomOut = () => {
  zoomAt(
    zoom.value * 0.9,
    (viewportRef.value?.clientWidth || 0) / 2,
    (viewportRef.value?.clientHeight || 0) / 2
  );
  scheduleSyncVisiblePages();
};

const onZoomIn = () => {
  zoomAt(
    zoom.value * 1.1,
    (viewportRef.value?.clientWidth || 0) / 2,
    (viewportRef.value?.clientHeight || 0) / 2
  );
  scheduleSyncVisiblePages();
};

const onZoomReset = () => {
  zoomTo(1);
  scheduleSyncVisiblePages();
};

const onResetView = () => {
  resetView();
  scheduleSyncVisiblePages();
};

const onActivatePage = pageId => {
  markPageMounted(pageId);
  emit('activatePage', pageId);
};

const feedbackChrome = computed(() => {
  if (!props.feedbackEnabled) return null;
  return {
    id: FEEDBACK_CHROME_ID,
    label: t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.FEEDBACK_CHROME'),
    selected: props.selectedId === FEEDBACK_CHROME_ID,
  };
});

const widgetInbox = computed(() => {
  if (!props.inboxId) return null;
  const inboxes = store.getters['inboxes/getInboxes'] || [];
  return (
    inboxes.find(inbox => String(inbox.id) === String(props.inboxId)) || null
  );
});

const widgetChrome = computed(() => {
  if (!props.inboxId) return null;
  const inbox = widgetInbox.value;
  return {
    id: WIDGET_CHROME_ID,
    label:
      inbox?.name || t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.WIDGET_CHROME'),
    selected: props.selectedId === WIDGET_CHROME_ID,
    color: inbox?.widget_color || props.theme?.accent_color || '#7c3aed',
    avatarUrl: inbox?.avatar_url,
  };
});

const onChromeClick = (event, id) => {
  event.stopPropagation();
  emit('select', id);
};

const onAddStage = stage => {
  stagePickerOpen.value = false;
  emit('addPage', stage);
  nextTick(() => {
    const last = props.pages[props.pages.length - 1];
    if (last) focusPage(last.id);
  });
};

const onReassignStage = stage => {
  if (!reassignPageId.value) return;
  emit('reassignPageStage', {
    pageId: reassignPageId.value,
    systemStage: stage,
  });
  reassignPageId.value = null;
};

const openReassign = page => {
  if (!page.systemStage) return;
  reassignPageId.value = page.id;
  stagePickerOpen.value = false;
};

const reassignOptions = computed(() => {
  const page = props.pages.find(p => p.id === reassignPageId.value);
  if (!page) return [];
  return SYSTEM_STAGES.filter(
    stage => stage === page.systemStage || props.availableStages.includes(stage)
  );
});

const onViewportPointerDown = e => {
  if (e.button !== 0) return;
  const onEmpty = e.target === viewportRef.value || e.target === worldRef.value;
  if (spaceHeld.value || onEmpty) {
    e.preventDefault();
    startPan(e.clientX, e.clientY);
  }
};

const onPointerMove = e => {
  movePan(e.clientX, e.clientY);
  scheduleSyncVisiblePages();
};

const onPointerUp = () => {
  endPan();
  scheduleSyncVisiblePages();
};

const onWheel = e => {
  const el = viewportRef.value;
  if (!el) return;

  if (e.ctrlKey || e.metaKey) {
    e.preventDefault();
    const rect = el.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    zoomAt(zoom.value * factor, localX, localY);
    scheduleSyncVisiblePages();
    return;
  }

  e.preventDefault();
  wheelPanBy(-e.deltaX, -e.deltaY, e.deltaMode);
  scheduleSyncVisiblePages();
};

const onKeyDown = e => {
  if (e.code === 'Space' && !e.repeat) {
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) {
      return;
    }
    setSpaceHeld(true);
  }
};

const onKeyUp = e => {
  if (e.code === 'Space') setSpaceHeld(false);
};

watch(
  () => props.pages.length,
  (next, prev) => {
    if (next > prev) {
      nextTick(() => {
        focusPage(props.activePageId);
        scheduleSyncVisiblePages();
      });
    } else {
      scheduleSyncVisiblePages();
    }
  }
);

watch(
  () => props.activePageId,
  () => {
    scheduleSyncVisiblePages();
  }
);

onMounted(() => {
  bindWorld(worldRef.value);
  bindViewport(viewportRef.value);
  bindZoomLabel(zoomLabelRef.value);
  loadStageColors();
  window.addEventListener('pointermove', onPointerMove, { passive: true });
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  nextTick(() => {
    bindWorld(worldRef.value);
    bindViewport(viewportRef.value);
    bindZoomLabel(zoomLabelRef.value);
    centerAtFullZoom();
    scheduleSyncVisiblePages();
  });
});

onBeforeUnmount(() => {
  if (syncVisibleRaf) {
    cancelAnimationFrame(syncVisibleRaf);
    syncVisibleRaf = 0;
  }
  window.removeEventListener('pointermove', onPointerMove);
  window.removeEventListener('pointerup', onPointerUp);
  window.removeEventListener('keydown', onKeyDown);
  window.removeEventListener('keyup', onKeyUp);
  dispose();
});
</script>

<template>
  <div class="flex-1 flex flex-col overflow-hidden relative">
    <div
      ref="viewportRef"
      data-portal-editor-viewport
      class="flex-1 overflow-hidden cursor-grab bg-[#f4f5f7] [background-image:radial-gradient(#d8dbe2_1px,transparent_1px)] [background-size:16px_16px]"
      @pointerdown="onViewportPointerDown"
      @wheel="onWheel"
      @click="
        () => {
          stagePickerOpen = false;
          reassignPageId = null;
        }
      "
    >
      <div ref="worldRef" class="absolute top-0 left-0 will-change-transform">
        <div class="flex items-start gap-16">
          <div
            v-for="(page, index) in pages"
            :key="page.id"
            :ref="el => setPageShellEl(page.id, el)"
            class="flex flex-col"
            :class="deviceMode === 'mobile' ? 'w-[390px]' : 'w-[1152px]'"
          >
            <div
              class="flex items-center gap-2 mb-2 h-8 select-none"
              @pointerdown.stop
              @click.stop="onActivatePage(page.id)"
            >
              <div class="relative min-w-0 max-w-[70%]">
                <button
                  type="button"
                  class="text-xs font-semibold truncate max-w-full rounded-md px-2 py-1 shadow-sm transition-opacity hover:opacity-90"
                  :class="
                    page.systemStage ? 'cursor-pointer' : 'cursor-default'
                  "
                  :style="pageLabelStyle(page)"
                  :title="
                    page.systemStage
                      ? $t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.REASSIGN')
                      : undefined
                  "
                  @click.stop="
                    page.systemStage
                      ? openReassign(page)
                      : onActivatePage(page.id)
                  "
                >
                  {{ pageLabel(page) }}
                </button>

                <div
                  v-if="reassignPageId === page.id"
                  class="absolute left-0 top-full mt-1 z-50 min-w-44 p-1 rounded-lg bg-white border border-n-slate-5 shadow-lg"
                  @click.stop
                  @pointerdown.stop
                >
                  <p
                    class="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
                  >
                    {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.PICK_STAGE') }}
                  </p>
                  <button
                    v-for="stage in reassignOptions"
                    :key="stage"
                    type="button"
                    class="flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-left text-xs font-medium text-n-slate-12 hover:bg-n-slate-2 transition-colors"
                    @click="onReassignStage(stage)"
                  >
                    <span
                      class="size-2.5 rounded-full shrink-0"
                      :style="stageSwatchStyle(stage)"
                    />
                    {{ stageLabel(stage) }}
                  </button>
                </div>
              </div>

              <div class="flex-1" />

              <button
                v-if="page.systemStage"
                type="button"
                class="flex items-center justify-center size-9 rounded-lg text-n-slate-9 hover:bg-n-ruby-2 hover:text-n-ruby-11 transition-colors"
                :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.DELETE')"
                @click.stop="emit('removePage', page.id)"
              >
                <span class="i-lucide-trash-2 size-5" />
              </button>

              <div v-if="index === pages.length - 1" class="relative">
                <button
                  type="button"
                  class="flex items-center justify-center size-9 rounded-lg shadow-sm transition-colors"
                  :class="
                    canAddPage
                      ? 'bg-n-brand text-white hover:bg-n-brand/90'
                      : 'bg-n-slate-4 text-n-slate-8 cursor-not-allowed'
                  "
                  :disabled="!canAddPage"
                  :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.ADD')"
                  @click.stop="stagePickerOpen = !stagePickerOpen"
                >
                  <span class="i-lucide-plus size-5" />
                </button>

                <div
                  v-if="stagePickerOpen && canAddPage"
                  class="absolute right-0 top-full mt-1 z-50 min-w-44 p-1 rounded-lg bg-white border border-n-slate-5 shadow-lg"
                  @click.stop
                >
                  <p
                    class="px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
                  >
                    {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.PICK_STAGE') }}
                  </p>
                  <button
                    v-for="stage in availableStages"
                    :key="stage"
                    type="button"
                    class="flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-left text-xs font-medium text-n-slate-12 hover:bg-n-slate-2 transition-colors"
                    @click="onAddStage(stage)"
                  >
                    <span
                      class="size-2.5 rounded-full shrink-0"
                      :style="stageSwatchStyle(stage)"
                    />
                    {{ stageLabel(stage) }}
                  </button>
                </div>
              </div>
            </div>

            <EditorPageArtboard
              :nodes="nodesForPage(page)"
              :nodes-revision="
                activePageId === page.id ? nodesRevision : undefined
              "
              :selected-id="activePageId === page.id ? selectedId : null"
              :components="components"
              :device-mode="deviceMode"
              :theme="theme"
              :template-id="templateId"
              :selected-event-id="selectedEventId"
              :portal-editable-attribute-keys="portalEditableAttributeKeys"
              :active="activePageId === page.id"
              :frame-mounted="isPageFrameMounted(page.id)"
              @activate="onActivatePage(page.id)"
              @select="emit('select', $event)"
              @deselect="emit('deselect')"
              @insert="emit('insert', $event)"
              @move="emit('move', $event)"
              @remove="emit('remove', $event)"
              @duplicate="emit('duplicate', $event)"
              @update-spans="emit('updateSpans', $event)"
              @update-content="emit('updateContent', $event)"
              @content-height="h => onPageContentHeight(page.id, h)"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Fixed chrome mocks (match public fixed positioning, stay in view) -->
    <button
      v-if="feedbackChrome"
      type="button"
      class="absolute top-4 right-4 z-[35] flex items-center gap-1.5 rounded-full border bg-white/95 px-3 py-1.5 text-[11px] font-medium shadow-sm"
      :class="
        feedbackChrome.selected
          ? 'border-[#3A96DD] text-n-slate-12 ring-2 ring-[#3A96DD]'
          : 'border-n-slate-5 text-n-slate-11'
      "
      @pointerdown.stop
      @click="onChromeClick($event, FEEDBACK_CHROME_ID)"
    >
      {{ feedbackChrome.label }}
      <span class="i-lucide-chevron-down size-3" />
    </button>

    <button
      v-if="widgetChrome"
      type="button"
      class="absolute bottom-16 right-5 z-[35] flex flex-col items-end gap-2"
      @pointerdown.stop
      @click="onChromeClick($event, WIDGET_CHROME_ID)"
    >
      <span
        class="rounded-full border bg-white/95 px-2.5 py-1 text-[10px] font-semibold shadow-sm max-w-[160px] truncate"
        :class="
          widgetChrome.selected
            ? 'border-[#3A96DD] text-n-slate-12 ring-2 ring-[#3A96DD]'
            : 'border-n-slate-5 text-n-slate-11'
        "
      >
        {{ widgetChrome.label }}
      </span>
      <span
        class="flex items-center justify-center size-14 rounded-full shadow-lg ring-2 ring-white overflow-hidden"
        :class="widgetChrome.selected ? 'ring-[#3A96DD]' : ''"
        :style="{ backgroundColor: widgetChrome.color }"
      >
        <img
          v-if="widgetChrome.avatarUrl"
          :src="widgetChrome.avatarUrl"
          alt=""
          class="size-full object-cover"
        />
        <span v-else class="i-lucide-message-circle size-6 text-white" />
      </span>
    </button>

    <div
      class="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 p-0.5 rounded-lg bg-white border border-n-slate-5 shadow-sm"
      @pointerdown.stop
      @click.stop
    >
      <button
        type="button"
        class="flex items-center justify-center size-7 rounded-md text-n-slate-10 hover:bg-n-slate-2 hover:text-n-slate-12 transition-colors"
        :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.ZOOM_OUT')"
        @click="onZoomOut"
      >
        <span class="i-lucide-minus size-3.5" />
      </button>
      <button
        ref="zoomLabelRef"
        type="button"
        class="min-w-12 px-1.5 py-1 text-[11px] font-medium text-n-slate-11 hover:bg-n-slate-2 rounded-md transition-colors"
        :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.ZOOM_RESET')"
        @click="onZoomReset"
      >
        100%
      </button>
      <button
        type="button"
        class="flex items-center justify-center size-7 rounded-md text-n-slate-10 hover:bg-n-slate-2 hover:text-n-slate-12 transition-colors"
        :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.ZOOM_IN')"
        @click="onZoomIn"
      >
        <span class="i-lucide-plus size-3.5" />
      </button>
      <div class="w-px h-4 bg-n-slate-5 mx-0.5" />
      <button
        type="button"
        class="flex items-center justify-center size-7 rounded-md text-n-slate-10 hover:bg-n-slate-2 hover:text-n-slate-12 transition-colors"
        :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.ZOOM_FIT')"
        @click="fitCanvas"
      >
        <span class="i-lucide-scan size-3.5" />
      </button>
      <button
        type="button"
        class="flex items-center justify-center size-7 rounded-md text-n-slate-10 hover:bg-n-slate-2 hover:text-n-slate-12 transition-colors"
        :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PAGES.ZOOM_RESET')"
        @click="onResetView"
      >
        <span class="i-lucide-locate-fixed size-3.5" />
      </button>
    </div>
  </div>
</template>
